import React, { useState, useEffect } from 'react';
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin, Heart, MessageCircle, Clock, X, Send } from 'lucide-react';
import { ChevronsUp } from 'lucide-react';
import { usePublic } from '../../../../context/PublicContext';
import { env } from '../../../../config/env';

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

const Footer: React.FC = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppMessage, setWhatsAppMessage] = useState('');

  const { settings, isLoadingSettings } = usePublic();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSendWhatsApp = () => {
    if (!settings?.whatsapp_number) {
      alert('Numéro WhatsApp non configuré');
      return;
    }

    const phone = settings.whatsapp_number.replace(/\D/g, '');
    const text = encodeURIComponent(whatsAppMessage || 'Bonjour, je viens de votre site web.');
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
    setShowWhatsAppModal(false);
    setWhatsAppMessage('');
  };

  if (isLoadingSettings) {
    return (
      <footer className="bg-accent/70 text-primary py-6 p-4 mt-5">
        <div className="max-w-7xl mx-auto text-center">
          <p>Chargement des informations...</p>
        </div>
      </footer>
    );
  }

  const companyName = settings?.company_name || "L'EFFICACITE DES PSAUMES ET PLANTES";
  const slogan = settings?.slogan || "L'alliance sacrée des Psaumes et des plantes médicinales pour votre bien-être quotidien.";
  const logoUrl = settings?.logo ? `${env.IMAGE_BASE_URL_SANS_STORAGE}${settings.logo}` : "/assets/logo/logoPsaumesetplantes-removebg-preview.png";
  const email = settings?.email || "judesessou@gmail.com";
  const phone = settings?.phone || "+229 01 97 73 77 03<br/>+225 05 84 74 00 00";
  const address = settings?.address || "123 Rue Tori, Bénin";
  const whatsappNumber = settings?.whatsapp_number || "2290197737703";
  const businessHours = settings?.business_hours;

  const socialLinks = settings?.social_links || {
    facebook: "https://www.facebook.com/j2d2s/",
    instagram: "https://www.instagram.com/j2d2s/",
    linkedin: "",
    youtube: "",
    tiktok: ""
  };
console.log('Logo URL from API:', settings?.logo);
console.log('Final logoUrl:', logoUrl);
  return (
    
    <footer className="bg-accent/70 text-primary py-6 p-4 mt-5">
      <div className="max-w-7xl mx-auto">
        {/* Contenu principal - 4 colonnes sur desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Colonne 1 : Logo + description (visible partout) */}
          <div className='space-y-3'>
            <div className="flex items-center justify-center cursor-pointer" onClick={() => window.location.href = '/'}>
              <img
                src={logoUrl}
                alt={companyName}
                className="h-24 w-auto object-contain"
              />
              <span className="text-2xl font-bold tracking-tight">{companyName}</span>
            </div>
            <p className="text-primary/70 text-sm leading-relaxed">
              {slogan}
            </p>
          </div>

          {/* Colonne 2 : Liens rapides - CACHÉ sur mobile car repris dans le grid mobile */}
          <div className='md:ml-12 hidden md:block'>
            <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
              Liens utiles
            </h3>
            <ul className="space-y-2 text-sm">
              <li><a href="/" className="text-primary/70 hover:text-secondary transition">Accueil</a></li>
              <li><a href="/about" className="text-primary/70 hover:text-secondary transition">À propos</a></li>
              <li><a href="/products" className="text-primary/70 hover:text-secondary transition">Nos produits</a></li>
              <li><a href="/contact" className="text-primary/70 hover:text-secondary transition">Contact</a></li>
            </ul>
          </div>

          {/* Colonne 3 : Contact + Réseaux sociaux (visible partout) */}
          <div className='flex flex-col gap-4'>
            <div>
              <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
                Contact
              </h3>
              <ul className="space-y-2 text-sm">
                {phone.split('<br/>').map((p, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-primary/70">
                    <Phone size={16} className="text-primary shrink-0" />
                    <span className="break-words">{p}</span>
                  </li>
                ))}
                <a href={`mailto:${email}`} className="flex items-center gap-2 text-primary/70 hover:text-secondary transition break-all">
                  <Mail size={16} className="text-primary shrink-0" />
                  {email}
                </a>
                <li className="flex items-center gap-2 text-primary/70">
                  <MapPin size={16} className="text-primary shrink-0" />
                  <span className="break-words">{address}</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-md font-semibold mb-2">Suivez-nous</h3>
              <div className="flex gap-3 flex-wrap">
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <Facebook size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <Instagram size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
                {socialLinks.youtube && (
                  <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <Youtube size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
                {socialLinks.tiktok && (
                  <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  </a>
                )}
                {whatsappNumber && (
                  <a href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <MessageCircle size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Colonne 4 : Horaires - CACHÉ sur mobile car repris dans le grid mobile */}
          {businessHours && (
            <div className="hidden md:block">
              <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
                Horaires
              </h3>
              <ul className="space-y-1 text-sm">
                <li className="flex justify-between"><span>Lundi :</span> <span>{businessHours.monday}</span></li>
                <li className="flex justify-between"><span>Mardi :</span> <span>{businessHours.tuesday}</span></li>
                <li className="flex justify-between"><span>Mercredi :</span> <span>{businessHours.wednesday}</span></li>
                <li className="flex justify-between"><span>Jeudi :</span> <span>{businessHours.thursday}</span></li>
                <li className="flex justify-between"><span>Vendredi :</span> <span>{businessHours.friday}</span></li>
                <li className="flex justify-between"><span>Samedi :</span> <span>{businessHours.saturday}</span></li>
                <li className="flex justify-between"><span>Dimanche :</span> <span>{businessHours.sunday}</span></li>
              </ul>
            </div>
          )}
        </div>

        {/* Version mobile UNIQUEMENT : Liens utiles + Horaires (2 colonnes) */}
        <div className="grid grid-cols-2 gap-8 mt-8 md:hidden">
          <div>
            <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
              Liens utiles
            </h3>
            <ul className="space-y-2 text-sm">
              <li><a href="/" className="text-primary/70 hover:text-secondary transition">Accueil</a></li>
              <li><a href="/about" className="text-primary/70 hover:text-secondary transition">À propos</a></li>
              <li><a href="/products" className="text-primary/70 hover:text-secondary transition">Nos produits</a></li>
              <li><a href="/contact" className="text-primary/70 hover:text-secondary transition">Contact</a></li>
            </ul>
          </div>
          {businessHours && (
            <div className="relative pl-4 sm:pl-6">
              <div className="absolute left-0 top-0 h-full w-px bg-primary/70"></div>
              <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
                Horaires
              </h3>
              <ul className="space-y-1 text-xs">
                <li className="flex justify-between"><span>Lun :</span> <span>{businessHours.monday}</span></li>
                <li className="flex justify-between"><span>Mar :</span> <span>{businessHours.tuesday}</span></li>
                <li className="flex justify-between"><span>Mer :</span> <span>{businessHours.wednesday}</span></li>
                <li className="flex justify-between"><span>Jeu :</span> <span>{businessHours.thursday}</span></li>
                <li className="flex justify-between"><span>Ven :</span> <span>{businessHours.friday}</span></li>
                <li className="flex justify-between"><span>Sam :</span> <span>{businessHours.saturday}</span></li>
                <li className="flex justify-between"><span>Dim :</span> <span>{businessHours.sunday}</span></li>
              </ul>
            </div>
          )}
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 mt-8 pt-6 text-center text-primary text-sm">
          <p>&copy; {new Date().getFullYear()} {companyName}. Conçu avec <Heart size={12} className="inline text-primary" /> pour votre bien-être.</p>
        </div>

        {/* Bouton scroll to top */}
        {showScrollButton && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-2 right-6 bg-secondary text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 z-40 md:bottom-8 md:right-8"
            aria-label="Remonter en haut"
          >
            <ChevronsUp size={24} />
          </button>
        )}

        {/* Bouton WhatsApp flottant */}
        {whatsappNumber && (
          <button
            onClick={() => setShowWhatsAppModal(true)}
            className="fixed bottom-6 right-6 bg-secondary text-white p-3 rounded-full shadow-lg hover:scale-110 transition-all duration-300 z-40 md:bottom-8 md:right-28"
            aria-label="Contact WhatsApp"
          >
            <MessageCircle size={24} />
          </button>
        )}

        {/* Modal WhatsApp - Positionné en bas à droite */}
        {showWhatsAppModal && (
          <>
            {/* Overlay transparent (juste pour fermer en cliquant ailleurs) */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowWhatsAppModal(false)}
            />
            
            {/* Modal en bas à droite */}
            <div className="fixed bottom-24 right-6 md:bottom-28 md:right-28 z-50 w-80 bg-white rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200">
              <div className="bg-green-800 text-white px-4 py-3 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <MessageCircle size={20} />
                  <span className="font-semibold">WhatsApp</span>
                </div>
                <button 
                  onClick={() => setShowWhatsAppModal(false)} 
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-4">
                <p className="text-gray-600 text-sm mb-3">Envoyez-nous un message</p>
                <textarea
                  value={whatsAppMessage}
                  onChange={(e) => setWhatsAppMessage(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                  placeholder="Bonjour, je souhaite en savoir plus sur vos produits..."
                />
                <button
                  onClick={handleSendWhatsApp}
                  className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm"
                >
                  <Send size={16} /> Envoyer
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </footer>
  );
};

export default Footer;